import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  UserAddIcon,
  TrashIcon,
  PencilIcon,
  ShieldCheckIcon,
  UserIcon,
  XIcon
} from '@heroicons/react/outline';

// Define User interface based on backend model
interface User {
  id: string;
  name: string;
  email: string;
  role: 'user' | 'admin';
  createdAt: string;
}

const UsersPage: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [showEditUserModal, setShowEditUserModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [formState, setFormState] = useState({
    name: '',
    email: '',
    password: '',
    role: 'user'
  });

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/admin/users');
      
      if (response.data.success) {
        setUsers(response.data.data);
      } else {
        setError('Failed to load users');
      }
    } catch (err: any) {
      setError(
        err.response?.data?.error || 
        'An error occurred while fetching users'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormState(prev => ({ ...prev, [name]: value }));
  };

  const resetForm = () => {
    setFormState({
      name: '',
      email: '',
      password: '',
      role: 'user'
    });
  };

  const openAddUserModal = () => {
    resetForm();
    setShowAddUserModal(true);
  };

  const openEditUserModal = (user: User) => {
    // Verify user has a valid ID
    if (!user || !user.id) {
      setError('Cannot edit user: Invalid user ID');
      return;
    }

    setCurrentUser(user);
    setFormState({
      name: user.name,
      email: user.email,
      password: '',
      role: user.role
    });
    setShowEditUserModal(true);
  };

  const handleAddUser = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    
    try {
      const response = await axios.post('/api/admin/users', formState);
      
      if (response.data.success) {
        setUsers(prev => [...prev, response.data.data]);
        setShowAddUserModal(false);
        resetForm();
      } else {
        setError('Failed to add user');
      }
    } catch (err: any) {
      console.error('Add user error:', err);
      
      // Handle validation errors
      if (err.response?.data?.validationErrors) {
        const validationErrors = err.response.data.validationErrors;
        const errorMessages = Object.values(validationErrors).join(', ');
        setError(`Validation error: ${errorMessages}`);
      } else {
        setError(
          err.response?.data?.error || 
          'An error occurred while adding the user'
        );
      }
    }
  };

  const handleUpdateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!currentUser) return;
    setError(null);
    
    try {
      // Only include password if it has a value
      const userData = formState.password
        ? formState
        : {
            name: formState.name,
            email: formState.email,
            role: formState.role
          };
      
      const response = await axios.put(`/api/admin/users/${currentUser.id}`, userData);
      
      if (response.data.success) {
        setUsers(prev => 
          prev.map(user => 
            user.id === currentUser.id ? response.data.data : user
          )
        );
        setShowEditUserModal(false);
        resetForm();
        setCurrentUser(null);
      } else {
        setError('Failed to update user');
      }
    } catch (err: any) {
      console.error('Update user error:', err);
      
      // Handle validation errors
      if (err.response?.data?.validationErrors) {
        const validationErrors = err.response.data.validationErrors;
        const errorMessages = Object.values(validationErrors).join(', ');
        setError(`Validation error: ${errorMessages}`);
      } else {
        setError(
          err.response?.data?.error || 
          'An error occurred while updating the user'
        );
      }
    }
  };

  const handleDeleteUser = async (userId: string) => {
    if (!window.confirm('Are you sure you want to delete this user?')) {
      return;
    }
    
    try {
      const response = await axios.delete(`/api/admin/users/${userId}`);
      
      if (response.data.success) {
        setUsers(prev => prev.filter(user => user.id !== userId));
      } else {
        setError('Failed to delete user');
      }
    } catch (err: any) {
      setError(
        err.response?.data?.error || 
        'An error occurred while deleting the user'
      );
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-ZA', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const filteredUsers = users.filter(user =>
    searchQuery === '' ||
    user.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    user.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
    user.role.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-xl text-foreground dark:text-dark-foreground">
          Loading users...
        </div>
      </div>
    );
  }

  return (
    <div className="pb-20 space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-foreground dark:text-dark-foreground">
          User Management
        </h1>
        <button
          onClick={openAddUserModal}
          className="inline-flex items-center px-4 py-2 text-sm font-medium text-white rounded-md bg-primary dark:bg-dark-primary hover:bg-primary/90 dark:hover:bg-dark-primary/90"
        >
          <UserAddIcon className="w-5 h-5 mr-2" />
          Add User
        </button>
      </div>

      {error && (
        <div className="p-4 rounded-lg bg-destructive/10 dark:bg-dark-destructive/10">
          <p className="text-destructive dark:text-dark-destructive">{error}</p>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-col gap-4 md:flex-row md:items-center">
        <div className="md:flex-1">
          <label htmlFor="search" className="sr-only">Search users</label>
          <input
            type="text"
            id="search"
            placeholder="Search by name, email, or role..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full p-2 border rounded-md border-input dark:border-dark-input bg-background dark:bg-dark-background text-foreground dark:text-dark-foreground"
          />
        </div>
      </div>

      {/* Users List */}
      <div className="overflow-hidden border rounded-lg shadow border-border dark:border-dark-border">
        {filteredUsers.length === 0 ? (
          <div className="flex items-center justify-center p-8">
            <p className="text-muted-foreground dark:text-dark-muted-foreground">
              No users found matching your criteria
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead className="bg-muted dark:bg-dark-muted">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground dark:text-dark-muted-foreground uppercase tracking-wider">
                    Name
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground dark:text-dark-muted-foreground uppercase tracking-wider">
                    Email
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground dark:text-dark-muted-foreground uppercase tracking-wider">
                    Role
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground dark:text-dark-muted-foreground uppercase tracking-wider">
                    Created
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-muted-foreground dark:text-dark-muted-foreground uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-card dark:bg-dark-card divide-y divide-border dark:divide-dark-border">
                {filteredUsers.map((user) => (
                  <tr key={user.id} className="hover:bg-muted/50 dark:hover:bg-dark-muted/50">
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-foreground dark:text-dark-foreground">
                        {user.name}
                      </div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="text-sm text-foreground dark:text-dark-foreground">
                        {user.email}
                      </div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        user.role === 'admin' 
                          ? 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200' 
                          : 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
                      }`}>
                        {user.role === 'admin' ? <ShieldCheckIcon className="w-3 h-3 mr-1" /> : <UserIcon className="w-3 h-3 mr-1" />}
                        {user.role.charAt(0).toUpperCase() + user.role.slice(1)}
                      </span>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="text-sm text-muted-foreground dark:text-dark-muted-foreground">
                        {formatDate(user.createdAt)}
                      </div>
                    </td>
                    <td className="px-4 py-4 text-right whitespace-nowrap">
                      <div className="flex items-center justify-end space-x-2">
                        <button
                          onClick={() => openEditUserModal(user)}
                          className="p-1 text-blue-600 rounded hover:bg-blue-100 dark:text-blue-400 dark:hover:bg-blue-900/30"
                          title="Edit user"
                        >
                          <PencilIcon className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => handleDeleteUser(user.id)}
                          className="p-1 text-red-600 rounded hover:bg-red-100 dark:text-red-400 dark:hover:bg-red-900/30"
                          title="Delete user"
                        >
                          <TrashIcon className="w-5 h-5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Add User Modal */}
      {showAddUserModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-foreground/50 dark:bg-dark-foreground/50">
          <div className="w-full max-w-md p-6 bg-card dark:bg-dark-card rounded-lg shadow-lg">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-medium text-foreground dark:text-dark-foreground">
                Add New User
              </h2>
              <button
                onClick={() => setShowAddUserModal(false)}
                className="text-muted-foreground dark:text-dark-muted-foreground hover:text-foreground dark:hover:text-dark-foreground"
              >
                <XIcon className="w-5 h-5" />
              </button>
            </div>
            
            <form onSubmit={handleAddUser} className="space-y-4">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-muted-foreground dark:text-dark-muted-foreground">
                  Name
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formState.name}
                  onChange={handleInputChange}
                  required
                  className="w-full p-2 mt-1 border rounded-md border-input dark:border-dark-input bg-background dark:bg-dark-background text-foreground dark:text-dark-foreground"
                />
              </div>
              
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-muted-foreground dark:text-dark-muted-foreground">
                  Email
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formState.email}
                  onChange={handleInputChange}
                  required
                  className="w-full p-2 mt-1 border rounded-md border-input dark:border-dark-input bg-background dark:bg-dark-background text-foreground dark:text-dark-foreground"
                />
              </div>
              
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-muted-foreground dark:text-dark-muted-foreground">
                  Password
                </label>
                <input
                  type="password"
                  id="password"
                  name="password"
                  value={formState.password}
                  onChange={handleInputChange}
                  required
                  className="w-full p-2 mt-1 border rounded-md border-input dark:border-dark-input bg-background dark:bg-dark-background text-foreground dark:text-dark-foreground"
                />
              </div>
              
              <div>
                <label htmlFor="role" className="block text-sm font-medium text-muted-foreground dark:text-dark-muted-foreground">
                  Role
                </label>
                <select
                  id="role"
                  name="role"
                  value={formState.role}
                  onChange={handleInputChange}
                  className="w-full p-2 mt-1 border rounded-md border-input dark:border-dark-input bg-background dark:bg-dark-background text-foreground dark:text-dark-foreground"
                >
                  <option value="user">User</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
              
              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowAddUserModal(false)}
                  className="px-4 py-2 text-sm font-medium text-foreground dark:text-dark-foreground bg-muted dark:bg-dark-muted hover:bg-muted/80 dark:hover:bg-dark-muted/80 rounded-md"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 text-sm font-medium text-white bg-primary dark:bg-dark-primary hover:bg-primary/90 dark:hover:bg-dark-primary/90 rounded-md"
                >
                  Add User
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {showEditUserModal && currentUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-foreground/50 dark:bg-dark-foreground/50">
          <div className="w-full max-w-md p-6 bg-card dark:bg-dark-card rounded-lg shadow-lg">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-medium text-foreground dark:text-dark-foreground">
                Edit User
              </h2>
              <button
                onClick={() => {
                  setShowEditUserModal(false);
                  setCurrentUser(null);
                }}
                className="text-muted-foreground dark:text-dark-muted-foreground hover:text-foreground dark:hover:text-dark-foreground"
              >
                <XIcon className="w-5 h-5" />
              </button>
            </div>
            
            <form onSubmit={handleUpdateUser} className="space-y-4">
              <div>
                <label htmlFor="edit-name" className="block text-sm font-medium text-muted-foreground dark:text-dark-muted-foreground">
                  Name
                </label>
                <input
                  type="text"
                  id="edit-name"
                  name="name"
                  value={formState.name}
                  onChange={handleInputChange}
                  required
                  className="w-full p-2 mt-1 border rounded-md border-input dark:border-dark-input bg-background dark:bg-dark-background text-foreground dark:text-dark-foreground"
                />
              </div>
              
              <div>
                <label htmlFor="edit-email" className="block text-sm font-medium text-muted-foreground dark:text-dark-muted-foreground">
                  Email
                </label>
                <input
                  type="email"
                  id="edit-email"
                  name="email"
                  value={formState.email}
                  onChange={handleInputChange}
                  required
                  className="w-full p-2 mt-1 border rounded-md border-input dark:border-dark-input bg-background dark:bg-dark-background text-foreground dark:text-dark-foreground"
                />
              </div>
              
              <div>
                <label htmlFor="edit-password" className="block text-sm font-medium text-muted-foreground dark:text-dark-muted-foreground">
                  Password <span className="text-xs text-muted-foreground dark:text-dark-muted-foreground">(Leave blank to keep current)</span>
                </label>
                <input
                  type="password"
                  id="edit-password"
                  name="password"
                  value={formState.password}
                  onChange={handleInputChange}
                  className="w-full p-2 mt-1 border rounded-md border-input dark:border-dark-input bg-background dark:bg-dark-background text-foreground dark:text-dark-foreground"
                />
              </div>
              
              <div>
                <label htmlFor="edit-role" className="block text-sm font-medium text-muted-foreground dark:text-dark-muted-foreground">
                  Role
                </label>
                <select
                  id="edit-role"
                  name="role"
                  value={formState.role}
                  onChange={handleInputChange}
                  className="w-full p-2 mt-1 border rounded-md border-input dark:border-dark-input bg-background dark:bg-dark-background text-foreground dark:text-dark-foreground"
                >
                  <option value="user">User</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
              
              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowEditUserModal(false);
                    setCurrentUser(null);
                  }}
                  className="px-4 py-2 text-sm font-medium text-foreground dark:text-dark-foreground bg-muted dark:bg-dark-muted hover:bg-muted/80 dark:hover:bg-dark-muted/80 rounded-md"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 text-sm font-medium text-white bg-primary dark:bg-dark-primary hover:bg-primary/90 dark:hover:bg-dark-primary/90 rounded-md"
                >
                  Update User
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default UsersPage; 